import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './context/ThemeContext';
import LandingPage from './components/LandingPage';
import Board from './components/Board';
import Navbar from './components/Navbar';
import Analytics from './components/Analytics';
import ParticlesBackground from './components/ParticlesBackground';
import AddTaskModal from './components/AddTaskModal';
import { loadData, saveData } from './utils/localStorage';
import { initialData } from './utils/data';
import { BoardData, Task } from './types';

const App: React.FC = () => {
  const [boardData, setBoardData] = useState<BoardData>(initialData);
  const [currentBoardId, setCurrentBoardId] = useState<string>(initialData.currentBoardId);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showLanding, setShowLanding] = useState<boolean>(true);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const savedData = loadData();
    if (savedData) {
      setBoardData(savedData);
    }
  }, []);

  useEffect(() => {
    if (boardData !== initialData) {
      saveData(boardData);
    }
  }, [boardData]);

  const updateBoardData = (newData: BoardData): void => {
    setBoardData(newData);
  };


  const handleGetStarted = () => {
    setShowLanding(false);
  };

  const handleBackToLanding = () => {
    setShowLanding(true);
  };

  const handleAddTask = () => {
    setIsAddTaskModalOpen(true);
  };

  const handleCloseAddTaskModal = () => {
    setIsAddTaskModalOpen(false);
  };

  const handleSaveTask = (taskData: Partial<Task>) => {
    const newTask: Task = {
      id: `task-${Date.now()}`,
      title: taskData.title || '',
      content: taskData.content || '',
      priority: taskData.priority || 'medium',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      columnId: taskData.columnId || 'todo',
      subtasks: [],
      tags: [],
      completed: false,
      assigneeIds: [],
      comments: [],
      attachments: []
    };

    const currentBoard = boardData.boards[boardData.currentBoardId];
    if (!currentBoard) return;

    const updatedTasks = {
      ...currentBoard.tasks,
      [newTask.id as string]: newTask
    };

    const currentColumn = currentBoard.columns[newTask.columnId as string];
    if (!currentColumn) return;

    const updatedColumns = {
      ...currentBoard.columns,
      [newTask.columnId as string]: {
        ...currentColumn,
        taskIds: [...currentColumn.taskIds, newTask.id]
      }
    };

    const updatedBoard = {
      ...currentBoard,
      tasks: updatedTasks,
      columns: updatedColumns,
      updatedAt: new Date().toISOString()
    };

    const updatedBoardData = {
      ...boardData,
      boards: {
        ...boardData.boards,
        [boardData.currentBoardId]: updatedBoard
      }
    };

    setBoardData(updatedBoardData);
    setIsAddTaskModalOpen(false);
  };

  const filteredTasks: Task[] = Object.values(boardData.boards[boardData.currentBoardId].tasks).filter(task =>
    task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    task.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (showLanding) {
    return (
      <ThemeProvider>
        <LandingPage onGetStarted={handleGetStarted} />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
        <ParticlesBackground />
        <div className="relative z-10">
          <Navbar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onBackToLanding={handleBackToLanding}
            onAddTask={handleAddTask}
          />

          <main className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-3">
                <Board
                  boardData={boardData}
                  updateBoardData={updateBoardData}
                  filteredTasks={filteredTasks}
                />
              </div>
              <div className="lg:col-span-1">
                <Analytics boardData={boardData} />
              </div>
            </div>
          </main>
        </div>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />

        <AddTaskModal
          isOpen={isAddTaskModalOpen}
          onClose={handleCloseAddTaskModal}
          onSave={handleSaveTask}
          editingTask={null}
          columns={boardData.boards[boardData.currentBoardId]?.columns || {}}
          users={boardData.boards[boardData.currentBoardId]?.users || {}}
        />
      </div>
    </ThemeProvider>
  );
};

export default App;
